import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, type Params, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { CoreModalService } from './core-modal.service';
import { type ModalWindowController } from './modal-window/modal-window.control';
import { type ModalConfig, type ModalInstance, type TypedModalConfig } from './modal.types';

/**
 * Keeps the address bar in sync with the open modals, so a modal can be opened from a link (deeplinking from emails).
 *
 * A modal declares the query parameters it owns through `ModalConfig.queryParams`:
 * opening the modal writes those parameters, closing it removes them again.
 * The other direction (address bar -> modals) is handled by the WebModalListenerService.
 */
@Injectable({
  providedIn: 'root',
})
export class WebModalService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private coreModalService = inject(CoreModalService);

  // emits the query parameters in the address bar, so the modal openers can open the modals that belong to them
  newQueryParams: ReplaySubject<Params> = new ReplaySubject(1);

  constructor() {
    this.coreModalService.dismissed$.subscribe({
      next: (modalInstance: ModalInstance) => {
        // unset query parameters if they were set by this modal
        if (modalInstance.config.queryParams) {
          this.unsetQueryParameters(modalInstance.config.queryParams);
        }
      },
    });
  }

  // shorthand method for simple modal
  showMiniModal(config: ModalConfig, outlet = 'root', parentController?: ModalWindowController): ReplaySubject<any> {
    return this.showModalOnce(config, () => this.coreModalService.showMiniModal(config, outlet, parentController));
  }

  // shorthand method for simple modal
  showSimpleModal(config: ModalConfig, outlet = 'root', parentController?: ModalWindowController): ReplaySubject<any> {
    return this.showModalOnce(config, () => this.coreModalService.showSimpleModal(config, outlet, parentController));
  }

  showTypedSimpleModal<C>(config: TypedModalConfig<C>, outlet = 'root', parentController?: ModalWindowController) {
    return this.showSimpleModal(config, outlet, parentController);
  }

  showModal(config: ModalConfig, outlet = 'root', parentController?: ModalWindowController): ReplaySubject<any> {
    return this.showModalOnce(config, () => this.coreModalService.showModal(config, outlet, parentController));
  }

  // Query Parameters

  // adds the parameters to the address bar, keeping every other parameter. A null value removes the parameter
  setQueryParameters(params: Params) {
    const currentParams = this.route.snapshot.queryParams;
    const newParams: Params = { ...currentParams };

    Object.keys(params).forEach((key) => {
      if (params[key] === null || params[key] === undefined) {
        delete newParams[key];
      } else {
        newParams[key] = params[key];
      }
    });

    this.navigateWithQueryParameters(newParams);
  }

  // removes the parameters that this modal still owns, and leaves every other parameter untouched
  unsetQueryParameters(unsetParams: Params) {
    const currentParams = this.route.snapshot.queryParams;
    const keptParams: Params = {};

    Object.keys(currentParams).forEach((key) => {
      // a parameter that no longer holds the value this modal set belongs to another modal now, so we keep it.
      // we also keep a parameter that a modal that is still open owns, even when this modal set the same value:
      // removing it would close that modal too
      if (
        !this.ownsQueryParameter(currentParams, key, unsetParams[key]) ||
        this.isOwnedByOpenModal(currentParams, key)
      ) {
        keptParams[key] = currentParams[key];
      }
    });

    // nothing of this modal is left in the address bar, so there is nothing to navigate away from.
    // a navigation to the same URL is skipped by the router, but only after it cancelled a navigation
    // that is already underway, which would undo a navigation that another modal closer just started
    if (Object.keys(keptParams).length === Object.keys(currentParams).length) {
      return;
    }

    this.navigateWithQueryParameters(keptParams);
  }

  // true when every query parameter of the modal is still in the address bar with the same value
  ownsQueryParameters(queryParams: Params, modal: ModalInstance): boolean {
    if (!modal.config.queryParams) {
      return false;
    }

    return Object.keys(modal.config.queryParams).every((key) =>
      // @ts-expect-error TS18048 possibly undefined
      this.ownsQueryParameter(queryParams, key, modal.config.queryParams[key]),
    );
  }

  /**
   * Opens the modal and writes its query parameters, unless a modal with the exact same query parameters
   * is already open. In that case we return the result of the modal that is already open.
   *
   * This keeps the modal openers simple: they react to every change in the address bar, including the
   * change their own modal just made, and this makes that second call a noop.
   */
  private showModalOnce(config: ModalConfig, showModal: () => ReplaySubject<any>) {
    if (!config.queryParams) {
      return showModal();
    }

    const alreadyOpenModal = this.coreModalService
      .modals()
      // @ts-expect-error TS2345 argument not assignable
      .find((modal) => this.hasSameQueryParameters(modal, config.queryParams));

    if (alreadyOpenModal) {
      return alreadyOpenModal.result;
    }

    this.setQueryParameters(config.queryParams);

    return showModal();
  }

  private hasSameQueryParameters(modal: ModalInstance, queryParams: Params): boolean {
    if (!modal.config.queryParams) {
      return false;
    }

    const keys = Object.keys(queryParams);

    if (keys.length !== Object.keys(modal.config.queryParams).length) {
      return false;
    }

    // @ts-expect-error TS18048 possibly undefined
    return keys.every((key) => this.queryParamHasSameValue(modal.config.queryParams[key], queryParams[key]));
  }

  private navigateWithQueryParameters(queryParams: Params) {
    // set the parameters by navigating to the current route with the new parameters
    this.router.navigate([], {
      queryParams,
      relativeTo: this.route,
      onSameUrlNavigation: 'ignore',
    });
  }

  // true when one of the modals that are still open owns this parameter.
  // the modal that is closing is already removed from the open modals, so it never matches itself
  private isOwnedByOpenModal(queryParams: Params, key: string): boolean {
    return this.coreModalService
      .modals()
      .some(
        (modal) =>
          modal.config.queryParams &&
          key in modal.config.queryParams &&
          this.ownsQueryParameter(queryParams, key, modal.config.queryParams[key]),
      );
  }

  // a modal owns a parameter when the address bar holds the exact value it set. A null value means it wants the parameter gone
  private ownsQueryParameter(queryParams: Params, key: string, ownedValue: any): boolean {
    if (ownedValue === null || ownedValue === undefined) {
      return queryParams[key] === undefined;
    }

    return this.queryParamHasSameValue(queryParams[key], ownedValue);
  }

  private queryParamHasSameValue(value: any, otherValue: any): boolean {
    const isValueEmpty = value === null || value === undefined;
    const isOtherValueEmpty = otherValue === null || otherValue === undefined;

    if (isValueEmpty || isOtherValueEmpty) {
      return isValueEmpty && isOtherValueEmpty;
    }

    // compare as strings, because the address bar holds Booleans and numbers as "true" \ "5"
    return value.toString() === otherValue.toString();
  }
}
